import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users } from "lucide-react";

/**
 * Renders a small pill showing how many testers are currently active,
 * with a tooltip listing their names. Polls /api/presence/active every 15s.
 * If `sessionId` is provided, also sends a heartbeat every 15s.
 */
export default function PresenceIndicator({ sessionId }) {
  const [active, setActive] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const tick = async () => {
      try {
        // Always send a heartbeat so the signed-in tester shows as active,
        // even outside the runner page. Use "idle" marker when no session.
        await api.heartbeat(sessionId || "idle");
        const list = await api.presenceActive();
        if (mounted) setActive(list);
      } catch {
        /* silent */
      }
    };

    tick();
    const t = setInterval(tick, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [sessionId]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="presence-indicator"
    >
      <button
        type="button"
        className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs font-mono border border-slate-200 hover:border-keplerio-teal/40 hover:bg-keplerio-teal/5 transition-colors"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full bg-keplerio-teal opacity-60" />
          <span className="relative inline-flex h-2 w-2 bg-keplerio-teal" />
        </span>
        <Users size={12} className="text-keplerio-navy" />
        <span className="text-keplerio-navy" data-testid="presence-count">
          {active.length} active
        </span>
      </button>
      {open && active.length > 0 && (
        <div
          className="absolute right-0 mt-1 z-50 w-64 bg-white border border-slate-200 shadow-lg"
          data-testid="presence-tooltip"
        >
          <div className="overline px-3 py-2 border-b border-slate-100">Testing right now</div>
          <ul className="max-h-56 overflow-auto">
            {active.map((p) => (
              <li key={p.user_id} className="px-3 py-2 text-xs flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-keplerio-teal shrink-0" />
                <div className="min-w-0">
                  <div className="text-slate-900 font-medium truncate">
                    {p.user_name || p.user_email}
                  </div>
                  {p.session_id && p.session_id !== "idle" && (
                    <div className="text-slate-400 font-mono truncate">
                      session {String(p.session_id).slice(0, 8)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
